import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { CrmCustomFiltersDescription } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-options";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import type { ResponseError } from "app/services/fetchService";

import { crmIntegrationQueryKeys } from "../key-factory/factory";

const fetchDescription = async (signal: AbortSignal): Promise<CrmCustomFiltersDescription> => {
    try {
        return await salesApiService.crmIntegration.fetchCrmCustomFiltersDescription({ cancellation: signal });
    } catch (error) {
        if ((error as ResponseError)?.status !== 403) {
            trackError(
                new SiError(
                    "Failed to fetch CRM custom filters description",
                    {
                        category: errorCategoryMap.api,
                        operation: "fetchCrmCustomFiltersDescription",
                    },
                    { cause: error },
                ),
            );
        }

        throw error;
    }
};

export default function useIntegrationCustomFiltersDescriptionQuery<TData = CrmCustomFiltersDescription>(
    select?: (data: CrmCustomFiltersDescription) => TData,
) {
    return useQuery({
        enabled: salesSettingsHelper.isCrmCustomFiltersEnabled,
        staleTime: Number.POSITIVE_INFINITY,
        placeholderData: keepPreviousData,
        queryKey: crmIntegrationQueryKeys.customFiltersDescription(),
        queryFn: ({ signal }) => fetchDescription(signal),
        select,
    });
}
